import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ComposedChart, Line, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceArea, ResponsiveContainer,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { getOutcomes } from "@/lib/api";

const fmt = (v) => (v === null || v === undefined ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)} D`);

const CHART_MARGIN = { top: 10, right: 16, bottom: 4, left: -8 };
const AXIS_TICK = { fontSize: 11 };
const X_LABEL = { value: "Case #", position: "insideBottomRight", offset: -2, fontSize: 11 };
const Y_DOMAIN = [-2, 2];
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8 };
const fmt1 = (v) => v.toFixed(1);

export default function OutcomesChart({ open, onOpenChange, defaultLensId }) {
  const [data, setData] = useState({ points: [], lenses: [] });
  const [lensId, setLensId] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getOutcomes()
      .then((d) => {
        setData(d);
        const has = (d.lenses || []).some((l) => l.lens_id === defaultLensId);
        setLensId(has ? defaultLensId : "all");
      })
      .catch(() => setData({ points: [], lenses: [] }))
      .finally(() => setLoading(false));
  }, [open, defaultLensId]);

  const series = useMemo(() => {
    const pts = (data.points || []).filter((p) => lensId === "all" || p.lens_id === lensId);
    let sum = 0;
    return pts.map((p, i) => {
      sum += p.prediction_error;
      return {
        idx: i + 1,
        pe: p.prediction_error,
        mean: lensId === "all" ? Math.round((sum / (i + 1)) * 1000) / 1000 : p.running_mean,
        label: `${p.patient_name || "—"} · ${p.side}`,
        date: new Date(p.created_at).toLocaleDateString(),
        lens: p.lens_name,
      };
    });
  }, [data, lensId]);

  const last = series[series.length - 1];
  const within05 = series.length ? Math.round((series.filter((s) => Math.abs(s.pe) <= 0.5).length / series.length) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="outcomes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl">
            <TrendingUp className="w-5 h-5 text-sky-700" /> Outcome trend
          </DialogTitle>
          <DialogDescription>
            Prediction error (achieved − target) for every logged eye, in chronological order, with the running mean per IOL model.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <Select value={lensId} onValueChange={setLensId}>
            <SelectTrigger className="w-72" data-testid="outcomes-lens-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All lenses</SelectItem>
              {(data.lenses || []).map((l) => (
                <SelectItem key={l.lens_id} value={l.lens_id} data-testid={`outcomes-lens-${l.lens_id}`}>
                  {l.lens_name} <span className="text-xs text-slate-500 font-mono ml-1">n={l.count}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto flex gap-4 text-xs text-slate-600">
            <Stat label="Eyes" value={series.length} testId="outcomes-count" />
            <Stat label="Running mean" value={fmt(last?.mean)} testId="outcomes-mean" />
            <Stat label="Within ±0.50 D" value={`${within05}%`} testId="outcomes-within" />
          </div>
        </div>

        <div className="h-72 w-full" data-testid="outcomes-chart">
          {loading ? (
            <div className="h-full grid place-items-center text-sm text-slate-500">Loading…</div>
          ) : series.length === 0 ? (
            <div className="h-full grid place-items-center text-sm text-slate-500 border border-dashed border-slate-300 rounded-md">
              No post-op refractions logged yet. Save one in the History drawer to start the trend.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={CHART_MARGIN}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <ReferenceArea y1={-0.5} y2={0.5} fill="#0ea5e9" fillOpacity={0.06} />
                <ReferenceLine y={0} stroke="#0f172a" strokeWidth={1} />
                <XAxis dataKey="idx" tick={AXIS_TICK} label={X_LABEL} />
                <YAxis tick={AXIS_TICK} domain={Y_DOMAIN} allowDataOverflow tickFormatter={fmt1} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v, name) => [fmt(v), name === "pe" ? "Prediction error" : "Running mean"]}
                  labelFormatter={(_, payload) => {
                    const p = payload?.[0]?.payload;
                    return p ? `${p.label} · ${p.date} · ${p.lens}` : "";
                  }}
                />
                <Scatter dataKey="pe" fill="#0369a1" shape="circle" />
                <Line type="monotone" dataKey="mean" stroke="#059669" strokeWidth={2.5} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="text-[11px] text-slate-500">
          Blue dots: individual eyes. Green line: running mean — it should settle towards 0.00 D as the A-constant is personalised.
          Shaded band: ±0.50 D.
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="font-mono text-sm text-slate-900" data-testid={testId}>{value}</div>
    </div>
  );
}
